<template>
  <div v-if="visible" class="upload-dialog-overlay" @click="closeIfOverlay">
    <div class="upload-dialog">
      <div class="upload-dialog-header">
        <h3>上传表情包</h3>
        <button class="close-button" @click="handleClose">&times;</button>
      </div>
      
      <div class="upload-dialog-body">
        <div 
          class="upload-drop-zone"
          @dragover.prevent
          @drop.prevent="handleFileDrop"
          @click="triggerFileInput"
          :class="{ 'active': isDragging }"
          @dragenter.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
        >
          <input 
            type="file" 
            ref="fileInput" 
            class="file-input" 
            accept="image/*" 
            @change="handleFileSelect"
            multiple
          >
          <div class="upload-icon">📁</div>
          <p>拖放图片到此处，或点击选择文件</p>
          <p class="upload-tip">支持粘贴上传，可以直接截图后 Ctrl+V 粘贴</p>
          <p class="upload-limit">文件大小限制：{{ maxFileSizeText }}以内</p>
        </div>
        
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
        
        <div v-if="previewUrl" class="image-preview">
          <h4>预览</h4>
          <img :src="previewUrl" alt="预览图">
          <div class="file-info" v-if="selectedFile">
            <p>文件名：{{ selectedFile.name }}</p>
            <p>大小：{{ formatFileSize(selectedFile.size) }}</p>
          </div>
        </div>
        
        <div class="upload-form" v-if="selectedFile">
          <div class="form-group">
            <label for="title">标题</label>
            <input 
              type="text" 
              id="title" 
              v-model="uploadForm.title" 
              placeholder="请输入表情包标题"
              class="form-input"
            >
            <p class="field-help">标题可选，如不填写将使用默认标题</p>
          </div>
        </div>
      </div>
      
      <div class="upload-dialog-footer">
        <button class="cancel-button" @click="handleClose">取消</button>
        <button 
          class="upload-button" 
          :disabled="!selectedFile || isUploading" 
          @click="handleUpload"
        >
          {{ isUploading ? '上传中...' : '上传' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { stickerApi } from '../services/api';
import authService from '../services/auth';

// 文件大小限制
const MAX_FILE_SIZE_NORMAL = 10 * 1024 * 1024; // 普通用户10MB
const MAX_FILE_SIZE_ADMIN = 30 * 1024 * 1024;  // 管理员30MB

export default {
  name: 'UploadDialog',
  inject: ['showNotification'],
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible', 'upload-success', 'close'],
  computed: {
    // 根据用户身份动态获取文件大小限制
    maxFileSize() {
      return authService.isAuthenticated() ? MAX_FILE_SIZE_ADMIN : MAX_FILE_SIZE_NORMAL;
    },
    // 格式化的文件大小限制文本
    maxFileSizeText() {
      return authService.isAuthenticated() ? '30MB' : '10MB';
    },
    // 检查用户是否为管理员
    isAdmin() {
      return authService.isAuthenticated();
    }
  },
  data() {
    return {
      selectedFile: null,
      previewUrl: '',
      uploadForm: {
        title: '',
      },
      isDragging: false,
      errorMessage: '',
      isUploading: false,
    }
  },
  mounted() {
    // 添加粘贴事件监听
    document.addEventListener('paste', this.handlePaste);
  },
  beforeUnmount() {
    // 移除粘贴事件监听
    document.removeEventListener('paste', this.handlePaste);
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndProcessFile(file);
      }
    },
    handleFileDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        this.validateAndProcessFile(file);
      }
    },
    handlePaste(event) {
      if (!this.visible) return;

      const items = (event.clipboardData || event.originalEvent.clipboardData).items;
      
      for (const item of items) {
        if (item.kind === 'file') {
          const blob = item.getAsFile();
          if (blob.type.indexOf('image/') === 0) {
            // 创建文件对象
            const pasteFile = new File([blob], `paste-${Date.now()}.png`, { 
              type: blob.type 
            });
            
            this.validateAndProcessFile(pasteFile);
            event.preventDefault();
            break;
          }
        }
      }
    },
    validateAndProcessFile(file) {
      // 清除之前的错误信息
      this.errorMessage = '';
      
      // 验证文件是否为图片
      if (!file.type.startsWith('image/')) {
        this.errorMessage = '只支持上传图片文件';
        return;
      }
      
      // 验证文件大小
      if (file.size > this.maxFileSize) {
        this.errorMessage = `文件大小超过限制，最大允许${this.maxFileSizeText}，当前文件: ${this.formatFileSize(file.size)}`;
        return;
      }
      
      // 如果验证通过，处理文件
      this.processSelectedFile(file);
    },
    processSelectedFile(file) {
      this.selectedFile = file;
      
      // 创建预览URL
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.previewUrl = URL.createObjectURL(file);
      
    //   // 如果没有设置标题且不是粘贴的文件，使用文件名作为默认标题
    //   if (!this.uploadForm.title && file.name && !file.name.startsWith('paste-')) {
    //     const fileName = file.name.split('.')[0];
    //     this.uploadForm.title = fileName;
    //   }
    },
    async handleUpload() {
      if (!this.selectedFile) {
        this.showNotification('请先选择要上传的文件', 'error');
        return;
      }
      
      try {
        this.isUploading = true;
        
        // 调用API上传文件
        const response = await stickerApi.uploadSticker(
          this.selectedFile,
          this.uploadForm.title
        );
        
        // 处理新的API响应格式
        if (response.data && response.data.code === 200) {
          // 上传成功
          console.log('上传成功:', response.data);
          
          // 触发上传成功事件
          this.$emit('upload-success', {
            file: this.selectedFile,
            formData: this.uploadForm,
            response: response.data.data || {} // 使用data字段中的数据
          });
          
          this.handleClose();
          this.showNotification('表情包上传成功！', 'success');
        } else {
          // API返回了错误码
          const errorMsg = response.data?.message || '上传失败，请稍后再试';
          throw new Error(errorMsg);
        }
      } catch (error) {
        console.error('上传失败:', error);
        
        // 显示错误信息
        const errorMsg = error.response?.data?.message || error.message || '上传失败，请稍后再试';
        this.errorMessage = errorMsg;
        this.showNotification(errorMsg, 'error');
      } finally {
        this.isUploading = false;
      }
    },
    handleClose() {
      // 清理预览URL
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }
      
      // 重置表单
      this.selectedFile = null;
      this.previewUrl = '';
      this.uploadForm.title = '';
      this.errorMessage = '';
      this.isUploading = false;
      
      // 关闭对话框
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    closeIfOverlay(event) {
      // 只有点击背景时才关闭
      if (event.target.classList.contains('upload-dialog-overlay')) {
        this.handleClose();
      }
    },
    formatFileSize(size) {
      if (size < 1024) {
        return size + ' B';
      } else if (size < 1024 * 1024) {
        return (size / 1024).toFixed(2) + ' KB';
      } else {
        return (size / (1024 * 1024)).toFixed(2) + ' MB';
      }
    }
  }
}
</script>

<style scoped>
.upload-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.upload-dialog {
  background-color: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

.upload-dialog-header {
  padding: 16px;
  border-bottom: 1px solid #e8f5e9;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.upload-dialog-header h3 {
  margin: 0;
  color: #42b983;
}

.close-button {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #999;
}

.close-button:hover {
  color: #333;
}

.upload-dialog-body {
  padding: 16px;
}

.upload-drop-zone {
  border: 2px dashed #42b983;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.3s, background-color 0.3s;
  position: relative;
}

.upload-drop-zone.active {
  border-color: #3da576;
  background-color: #f0f9f0;
}

.upload-drop-zone:hover {
  background-color: #f8f8f8;
}

.file-input {
  display: none;
}

.upload-icon {
  font-size: 40px;
  margin-bottom: 10px;
  color: #42b983;
}

.upload-tip {
  font-size: 12px;
  color: #999;
  margin-top: 10px;
}

.upload-limit {
  font-size: 12px;
  color: #ff5252;
  margin-top: 5px;
}

.error-message {
  background-color: #ffebee;
  color: #ff5252;
  padding: 10px;
  border-radius: 4px;
  margin-top: 15px;
  font-size: 14px;
}

.image-preview {
  margin-top: 20px;
  text-align: center;
}

.image-preview h4 {
  margin-bottom: 10px;
  color: #666;
}

.image-preview img {
  max-width: 100%;
  max-height: 200px;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.file-info {
  margin-top: 10px;
  font-size: 12px;
  color: #666;
}

.upload-form {
  margin-top: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: #666;
  font-weight: 500;
}

.form-input, .form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.field-help {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.form-textarea {
  min-height: 80px;
  resize: vertical;
}

.upload-dialog-footer {
  padding: 16px;
  border-top: 1px solid #e8f5e9;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.cancel-button {
  padding: 8px 16px;
  background-color: #f5f5f5;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.upload-button {
  padding: 8px 16px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.upload-button:disabled {
  background-color: #a8d5c2;
  cursor: not-allowed;
}

.cancel-button:hover {
  background-color: #e5e5e5;
}

.upload-button:hover:not(:disabled) {
  background-color: #3da576;
}
</style>
